import Navbar from "../../Navbar/NavbarComponent/Navbar";
import { useEffect, useMemo, useState } from "react";
import Table from "../../../shared/components/Table";
import {
  ACTION_LABEL,
  BUTTON_LABEL,
  icons,
  LABELS,
  SEARCH_DATA,
  toastError,
} from "../../../shared/constants";
import CustomModal from "../../../shared/components/CustomModal";
import Loader from "../../../shared/components/Loader";
import ButtonLoader from "../../../shared/components/ButtonLoader";
import Search from "../../../shared/components/Search";
import { skipToken } from "@reduxjs/toolkit/query";
import SimpleButton from "../../../shared/components/Button/Button";
import { Dropdown } from "react-bootstrap";
import authProvider from "../../../shared/config/authProvider";
import Filter from "../../Common/Filter/Filter";
import { DEBOUNCE_VALUE } from "../../../shared/constants/resource";
import useDebounce from "../../../shared/utils/hooks/useDebounce";
import { dateAndTimeConvert } from "../../../shared/helperFunc/dateConvert";
import { Rating } from "react-simple-star-rating";
import CustomerFeedbackDetail from "./CustomerFeedbackDetail";
import {
  FeedbackDetailItem,
  useCustomerFeedbackExportMutation,
  useCustomerFeedbackGetQuery,
  useCustomerFeedbackListQuery,
} from "../../../Features/customerFeedback/customer-feedback-slice";

const CustomerFeedbackList = () => {
  const [sort, setSort] = useState<any>(SEARCH_DATA);
  const [count, setCount] = useState<number>();
  const [feedbackList, setFeedbackList]: any = useState([]);
  const [showDetail, setShowDetail] = useState(false);
  const [feedbackId, setFeedbackId] = useState<string | undefined>();
  const [feedbackDetailData, setFeedbackDetailData] = useState<FeedbackDetailItem | undefined>();
  const [showFilter, setShowFilter] = useState(false);
  const [filterCalled, setFilterCalled]: any = useState(false);
  const [filterSelectionError, setFilterSelectionError]: any = useState();
  const [filterValues, setFilterValues] = useState<any>({});
  const [defaultFilterValues, setDefaultFilterValues] = useState<any>({});
  const [searchKeyword, setSearchKeyword] = useState(sort.search);
  const debouncedSearchKeyword = useDebounce(searchKeyword, DEBOUNCE_VALUE);

  const queryArgs = useMemo(() => {
    return {
      ...sort,
      search: debouncedSearchKeyword,
    };
  }, [debouncedSearchKeyword, sort]);

  const {
    data,
    isSuccess,
    isFetching: feedbackListFetching,
  } = useCustomerFeedbackListQuery(queryArgs, {
    refetchOnFocus: true,
    refetchOnMountOrArgChange: true,
  });

  const {
    data: getFeedbackData,
    isLoading: getFeedbackLoading,
    isSuccess: getFeedbackSuccess,
  } = useCustomerFeedbackGetQuery(feedbackId || skipToken, {
    refetchOnMountOrArgChange: true,
  });

  const [customerFeedbackExport, { isLoading: exportLoading }] =
    useCustomerFeedbackExportMutation();

  useEffect(() => {
    if (isSuccess) {
      setFeedbackList(data.list);
      setCount(data.count);
    }
  }, [data, isSuccess]);

  useEffect(() => {
    if (feedbackId && getFeedbackSuccess) {
      setFeedbackDetailData(getFeedbackData.item);
    }
  }, [feedbackId, getFeedbackData, getFeedbackSuccess]);

  const lowRatingActive = !!sort.filter?.lowRating;

  const exportFlow = (exportData: any) => {
    const downloadPath = process.env.REACT_APP_BACKEND_URL + "/uploads/";
    const a = document.createElement("a");
    a.href = downloadPath + exportData.item;
    a.download = exportData.file || exportData.item;
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  const handleExport = async (format: "csv" | "xlsx") => {
    const exportResponse = await customerFeedbackExport({
      format,
      search: sort.search,
      filter: sort.filter,
    })
      .unwrap()
      .catch((err: any) => {
        toastError(err.data?.errors?.[0]?.msg || "Export failed");
      });
    if (exportResponse && exportResponse?.status) {
      exportFlow(exportResponse);
    }
  };

  const handleApplyFilter = () => {
    const appliedFilter: any = {};
    if (sort.filter?.lowRating) {
      appliedFilter.lowRating = true;
    }
    if (filterValues.store) {
      appliedFilter.store = filterValues.store;
    }
    if (filterValues.orderNumber && filterValues.orderNumber.trim()) {
      appliedFilter.orderNumber = filterValues.orderNumber.trim();
    }
    if (filterValues.startDate) {
      appliedFilter.fromDate = filterValues.startDate;
    } else if (filterValues.dateRange?.from) {
      appliedFilter.fromDate = filterValues.dateRange.from;
    }
    if (filterValues.endDate) {
      appliedFilter.toDate = filterValues.endDate;
    } else if (filterValues.dateRange?.to) {
      appliedFilter.toDate = filterValues.dateRange.to;
    }
    setFilterCalled(!filterCalled);
    setSort({ ...sort, filter: appliedFilter, filterFlag: true, skip: 0 });
    setShowFilter(false);
  };

  const handleCallFilter = () => {
    if (!showFilter) {
      setFilterValues({
        store: sort.filter?.store,
        orderNumber: sort.filter?.orderNumber,
        startDate: sort.filter?.fromDate || null,
        endDate: sort.filter?.toDate || null,
      });
    }
    setShowFilter(!showFilter);
  };

  const resetFilter = () => {
    setFilterValues(defaultFilterValues);
    setSort({ ...sort, filter: defaultFilterValues, filterFlag: false });
    setFilterCalled(!filterCalled);
    setFilterSelectionError("");
    setShowFilter(false);
  };

  const closeFilter = () => {
    setShowFilter(false);
  };

  const handleClose = () => {
    setShowDetail(false);
    setFeedbackId(undefined);
    setFeedbackDetailData(undefined);
  };

  const handleSearchTextChange = (e: any) => {
    setSearchKeyword(e.target.value);
    setSort({ ...sort, search: e.target.value, skip: 0 });
  };

  const handleClearSearch = () => {
    setSearchKeyword("");
    setSort({ ...sort, search: "", skip: 0 });
  };

  const handleKeyDown = (e: any) => {
    if (e.key === "Enter") {
      e.preventDefault();
    }
  };

  const handleOnBlur = (e: any) => {
    const trimmedValue = e.target.value.trim();
    setFilterValues({ ...filterValues, [e.target.name]: trimmedValue });
  };

  const handleTextChange = (e: any) => {
    setFilterValues({ ...filterValues, [e.target.name]: e.target.value });
  };

  const handleSelectionSearch = (item: any, name: any) => {
    if (item?.value) {
      setFilterValues({ ...filterValues, [name]: item.value });
    } else {
      const updatedFilter: any = { ...filterValues };
      delete updatedFilter[name];
      setFilterValues(updatedFilter);
    }
    setFilterSelectionError("");
  };

  const handleStartDate = (date: any) => {
    const fromDate = date
      ? new Date(new Date(date).setHours(0, 0, 0, 0)).toISOString()
      : null;
    setFilterValues((prev: any) => ({ ...prev, startDate: fromDate }));
  };

  const handleEndDate = (date: any) => {
    const toDate = date
      ? new Date(new Date(date).setHours(23, 59, 59, 999)).toISOString()
      : null;
    setFilterValues((prev: any) => ({ ...prev, endDate: toDate }));
  };

  const toggleLowRating = (e?: any) => {
    e?.currentTarget?.blur();
    const nextLowRating = !lowRatingActive;
    const updatedFilter: any = { ...sort.filter };
    if (nextLowRating) {
      updatedFilter.lowRating = true;
    } else {
      delete updatedFilter.lowRating;
    }
    setSort({
      ...sort,
      filter: updatedFilter,
      skip: 0,
    });
  };

  const handleFeedbackDetails = (e: any, row: any) => {
    setFeedbackId(row.feedbackId);
    setShowDetail(true);
  };

  const filterContent = (
    handleTextChange: any,
    handleSelectionSearch: any,
    handleStartDate: any,
    handleEndDate: any
  ) => {
    return [
      {
        label: LABELS.STORE_NAME_TEXT,
        name: ACTION_LABEL.STORE,
        elementType: "selectSearch",
        action: handleSelectionSearch,
        type: "",
        operator: "is from",
        isSearch: true,
      },
      {
        label: LABELS.ORDER_NO_TEXT,
        name: ACTION_LABEL.ORDER_NUMBER,
        elementType: "text",
        action: handleTextChange,
        type: "",
        operator: "is from",
      },
      {
        label: LABELS.START_DATE_TEXT,
        name: ACTION_LABEL.START_DATE,
        elementType: "date",
        action: handleStartDate,
        type: "",
        operator: "is from",
        maxDate: filterValues?.endDate ? new Date(filterValues.endDate) : undefined,
      },
      {
        label: LABELS.END_DATE_TEXT,
        name: ACTION_LABEL.END_DATE,
        elementType: "date",
        action: handleEndDate,
        type: "",
        operator: "is from",
        minDate: filterValues?.startDate ? new Date(filterValues.startDate) : undefined,
      },
    ];
  };

  const columns = useMemo(
    () => [
      {
        header: LABELS.ORDER_NO_TEXT,
        cell: ({ row }: any) => row.original.orderNumber,
        isShow: true,
        enableSorting: false,
      },
      {
        header: LABELS.STORE_NAME_TEXT,
        cell: ({ row }: any) => row.original.storeName,
        isShow: true,
        enableSorting: false,
      },
      {
        header: LABELS.SUBMITTED_AT_TEXT,
        cell: ({ row }: any) => dateAndTimeConvert(row.original.submittedAt),
        isShow: true,
        enableSorting: false,
      },
      {
        header: LABELS.RATING_SUMMARY_TEXT,
        cell: ({ row }: any) => {
          const summary = row.original.ratingSummary;
          const avg = summary?.average || 0;
          const starColor =
            avg <= 1
              ? "#EF4444"
              : avg <= 2
              ? "#F97316"
              : avg <= 3
              ? "#F59E0B"
              : avg <= 4
              ? "#EAB308"
              : "#FFC107";
          return (
            <div className="d-flex align-items-center gap-2">
              <Rating
                readonly
                initialValue={avg}
                size={16}
                fillColor={starColor}
                emptyColor="#e5e7eb"
              />
              <span>
                {summary?.average != null
                  ? Number(summary.average).toFixed(1)
                  : "-"}
              </span>
              {summary?.lowRated && (
                <span className="badge bg-danger">{LABELS.LOW_RATED_TEXT}</span>
              )}
            </div>
          );
        },
        isShow: true,
        enableSorting: false,
      },
      {
        header: LABELS.COMMENT_PREVIEW_TEXT,
        cell: ({ row }: any) => {
          const preview = row?.original?.commentPreview;
          if (!preview) return "-";
          const MAX_LENGTH = 40;
          const isLong = preview.length > MAX_LENGTH;
          const display = isLong ? `${preview.slice(0, MAX_LENGTH)}...` : preview;
          return (
            <span
              title={preview}
              style={{
                display: "inline-block",
                maxWidth: "280px",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                verticalAlign: "middle",
              }}
            >
              {display}
            </span>
          );
        },
        isShow: true,
        enableSorting: false,
      },
    ],
    [sort]
  );

  return (
    <>
      <div className="full-height">
        <Navbar title={LABELS.CUSTOMER_FEEDBACK_LIST_TEXT} />
        <main className="main-content">
          <div className="pt-3">
            <div className="container-fluid">
              <div className="row table-top-actions">
                <div className="col-12 col-md-12 d-flex justify-content-end">
                  <div className="d-flex align-items-center row w-100 mx-m-0">
                    <Search
                      placeholder={LABELS.SEARCH_TEXT}
                      name={ACTION_LABEL.SEARCH}
                      value={sort.search}
                      onChange={handleSearchTextChange}
                      onKeyDown={handleKeyDown}
                      onClick={() => {}}
                      onClear={handleClearSearch}
                    />

                    {authProvider.show("feedback", "view") && (
                      <>
                        <Dropdown className="custom-dropdown d-flex align-items-center w-auto px-0 me-2">
                          <Dropdown.Toggle
                            as="div"
                            className="d-inline-block cursor-pointer caret-hide"
                          >
                            <SimpleButton
                              className={
                                "btn theme-btn theme-btn-sm btn-inverse w-auto"
                              }
                              disabled={exportLoading}
                              text={
                                exportLoading ? (
                                  <ButtonLoader />
                                ) : (
                                  BUTTON_LABEL.EXPORT || "Export"
                                )
                              }
                            >
                              {!exportLoading && (
                                <i
                                  className="icon-chevron-down ms-1"
                                  style={{ fontSize: "10px" }}
                                ></i>
                              )}
                            </SimpleButton>
                          </Dropdown.Toggle>
                          <Dropdown.Menu>
                            <Dropdown.Item
                              onClick={() => handleExport("xlsx")}
                            >
                              Excel
                            </Dropdown.Item>
                            <Dropdown.Item
                              onClick={() => handleExport("csv")}
                            >
                              CSV
                            </Dropdown.Item>
                          </Dropdown.Menu>
                        </Dropdown>
                        <SimpleButton
                          className={`me-2 btn theme-btn theme-btn-sm ${
                            lowRatingActive ? "" : "btn-inverse"
                          } w-auto`}
                          onClick={toggleLowRating}
                          text={LABELS.LOW_RATED_TEXT}
                        />
                        <SimpleButton
                          className={
                            "me-2 btn theme-btn theme-btn-sm btn-inverse filter-btn"
                          }
                          btnIcon={icons.ICON_FILTER}
                          btnIconClass={"me-2 d-inline-block"}
                          onClick={handleCallFilter}
                        />
                      </>
                    )}
                  </div>
                </div>
              </div>

              <div className="card custom-card card-full mt-3">
                <div className="card-body p-0">
                  <Table
                    data={feedbackList}
                    columns={columns}
                    sort={sort}
                    setSort={setSort}
                    coloumnDrop={false}
                    count={count}
                    isFetching={feedbackListFetching}
                    handleRow={handleFeedbackDetails}
                    isHandleRowEnabled
                  />
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>

      <CustomModal
        show={showDetail}
        close={handleClose}
        size={"lg custom-lg"}
        message={""}
        modalTitle={LABELS.CUSTOMER_FEEDBACK_LIST_TEXT}
        modalButton={[
          {
            text: BUTTON_LABEL.CLOSE,
            action: handleClose,
            className: "btn theme-btn",
          },
        ]}
        fullscreen={undefined}
        className={"light-modal inventory-detail-modal"}
        centerClass={""}
        modalTitleIcon={""}
      >
        <CustomerFeedbackDetail
          feedbackDetailData={feedbackDetailData}
          loading={getFeedbackLoading}
        />
      </CustomModal>

      <Filter
        sort={filterValues}
        filterSelectionError={filterSelectionError}
        filterObject={filterContent(
          handleTextChange,
          handleSelectionSearch,
          handleStartDate,
          handleEndDate
        )}
        show={showFilter}
        onClose={closeFilter}
        handleReset={resetFilter}
        handleApply={handleApplyFilter}
        handleOnBlur={handleOnBlur}
      />

      {exportLoading && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            backgroundColor: "rgba(255, 255, 255, 0.5)",
            zIndex: 9999,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Loader />
        </div>
      )}
    </>
  );
};

export default CustomerFeedbackList;
